import { useState, useEffect } from "react";
import ResourcePage from "@/components/ResourcePage";
import PageHeader from "@/components/PageHeader";
import { TEAM_ROLES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const fields = [
  { name: "name", label: "Nama", required: true },
  { name: "role", label: "Jabatan", type: "select", options: TEAM_ROLES, required: true, default: "Crew" },
  { name: "email", label: "Email (untuk login crew)", type: "email" },
  { name: "phone", label: "No. WhatsApp" },
  { name: "skill", label: "Skill" },
  { name: "status", label: "Status", type: "select", options: ["Active", "Inactive"], default: "Active" },
  { name: "photo", label: "Foto Profil", type: "file", full: true },
];

const baseColumns = [
  { key: "name", label: "Nama", render: (r) => <span className="font-semibold text-[#1A1A1A]">{r.name}</span> },
  { key: "role", label: "Jabatan" },
  { key: "phone", label: "WhatsApp" },
  { key: "skill", label: "Skill" },
  { key: "status", label: "Status", badge: true },
];

export default function Team() {
  const { can } = useAuth();
  const [acc, setAcc] = useState(null); // {member, exists, email}
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [accountIds, setAccountIds] = useState(new Set());

  const loadAccounts = () => api.get("/crew-account-ids").then((r) => setAccountIds(new Set(r.data.member_ids || []))).catch(() => {});
  useEffect(() => { loadAccounts(); }, []);

  const isCrewRole = (r) => (r.role || "").toLowerCase().includes("crew") || (r.role || "").toLowerCase().includes("coordinator");
  const columns = [
    baseColumns[0], baseColumns[1],
    { key: "email", label: "Email", render: (r) => {
      const linked = accountIds.has(r.id);
      return (
        <div className="flex flex-col gap-1">
          {r.email ? <span className="text-[#4A4A4A]">{r.email}</span> : <span className="text-[#B0B0B0] text-xs italic">—</span>}
          {isCrewRole(r) && (linked
            ? <span className="inline-flex items-center gap-1 w-fit text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="team-linked-yes"><CheckCircle2 className="w-3 h-3" />Tertaut akun</span>
            : <span className="inline-flex items-center gap-1 w-fit text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="team-linked-no"><KeyRound className="w-3 h-3" />{r.email ? "Belum ada akun" : "Belum ada email"}</span>)}
        </div>
      );
    } },
    baseColumns[2], baseColumns[3], baseColumns[4],
  ];

  const openAccount = async (member) => {
    setPwd("");
    let info = { exists: false, email: "" };
    try { info = (await api.get(`/team_members/${member.id}/account`)).data; } catch { /* noop */ }
    setAcc({ member, exists: info.exists, email: info.email || "" });
  };
  const saveAccount = async () => {
    if (!acc.email.trim() || pwd.length < 6) { toast.error("Email valid & password minimal 6 karakter"); return; }
    setSaving(true);
    try {
      await api.post(`/team_members/${acc.member.id}/account`, { email: acc.email.trim(), password: pwd });
      toast.success("Akun login crew berhasil dibuat");
      setAcc(null);
      loadAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat akun"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <ResourcePage
        resource="team_members" title="Anggota Tim" writeLevel={3}
        columns={columns} fields={fields}
        PageHeaderComp={<PageHeader overline="Tim Wedding Organizer" title="Team" subtitle="Kelola anggota tim, foto profil, dan akun login crew." />}
        rowActions={can(4) ? (row) => (
          <button onClick={() => openAccount(row)} title="Buat / atur akun login crew" data-testid="team-account-btn" className="p-1.5 text-[#7A7A7A] hover:text-burgundy">
            <KeyRound className="w-4 h-4" />
          </button>
        ) : undefined}
      />

      <Dialog open={!!acc} onOpenChange={(o) => !o && setAcc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-burgundy">Akun Login Crew</DialogTitle>
            <DialogDescription>Buat akun agar {acc?.member?.name} bisa masuk ke portal crew dan melihat tugasnya.</DialogDescription>
          </DialogHeader>
          {acc?.exists && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="team-account-exists">
              <CheckCircle2 className="w-4 h-4" />Akun sudah ada: {acc.email}. Simpan lagi untuk reset password.
            </div>
          )}
          <div className="space-y-3 py-1">
            <div><Label className="text-xs font-semibold mb-1 block">Email Login</Label><Input value={acc?.email || ""} onChange={(e) => setAcc({ ...acc, email: e.target.value })} placeholder="crew@email.com" data-testid="team-account-email" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Password {acc?.exists ? "Baru" : ""}</Label><Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Minimal 6 karakter" data-testid="team-account-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcc(null)}>Batal</Button>
            <Button onClick={saveAccount} disabled={saving} data-testid="team-account-save" className="bg-burgundy hover:bg-burgundy-dark text-white">{saving ? "Menyimpan..." : (acc?.exists ? "Perbarui Akun" : "Buat Akun")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
